<!-- MpfDialog.vue -->
<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref, unref, useSlots, watch } from 'vue';
  import type { MpfDialogProps, MpfDialogEmits } from './MpfDialog.props';
  import { MpfDialogDefaults } from './MpfDialog.props';

  import './MpfDialog.scss';

  const props = withDefaults(defineProps<MpfDialogProps>(), MpfDialogDefaults);
  const emit = defineEmits<MpfDialogEmits>();

  const dialogRef = ref<HTMLDialogElement | null>(null);

  let intervalId: ReturnType<Window['setInterval']> | null = null;
  const straplineRef = ref<HTMLElement | null>(null);
  const bodyRef = ref<HTMLElement | null>(null);
  const announcedText = ref('');

  const titleText = computed(() => unref(props.title));
  const strapline = computed(() => (props.straplineText ? unref(props.straplineText) : ''));

  const slots = useSlots();

  // TODO; make 'propable', unique
  const dialogId = 'mpf-dialog';
  const titleId = `${dialogId}-title`;
  const straplineId = `${dialogId}-strapline`;
  const bodyId = `${dialogId}-body`;

  function requestClose(reason: 'esc' | 'backdrop' | 'cancel') {
    emit('update:modelValue', false);
    writeToStatusAlert('');
    stopAnnounceInterval();
    props.closeDialog?.();
    props.onCancel?.(reason);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && props.closeOnEsc !== false) {
      e.preventDefault();
      requestClose('esc');
    }
  }

  function onCancelEvent(e: Event) {
    // <dialog> fires a cancel event on ESC by default (when open).
    // We centralise behaviour and prevent native auto-close.
    e.preventDefault();
    if (props.closeOnEsc !== false) requestClose('esc');
  }

  function onBackdropMouseDown(e: MouseEvent) {
    if (props.type !== 'modal') return;
    // Only treat clicks on the dialog surface (outside the content) as backdrop clicks.
    if (e.target === e.currentTarget) requestClose('backdrop');
  }

  async function onOkClick() {
    await props.onOk?.('ok');
  }

  async function onCancelClick() {
    requestClose('cancel');
  }

  function openDialog() {
    const el = dialogRef.value;
    if (!el || el.open) return;
    props.type === 'modal' ? el.showModal() : el.show();
    emit('opened');

    startAnnounceInterval();
  }

  function closeDialog() {
    const el = dialogRef.value;
    if (!el || !el.open) return;
    stopAnnounceInterval();
    el.close();
    emit('closed');
  }

  function writeToStatusAlert(text: string) {
    const el = document.getElementById(props.announceTargetId ?? '');
    if (!el) return;
    nextTick();
    el.textContent = text;
  }

  function startAnnounceInterval() {
    const cadence = props.announceWhileOpenMs ?? 0;
    if (!cadence || cadence <= 0) return;
    stopAnnounceInterval();
    writeToStatusAlert(bodyRef.value?.textContent?.trim() ?? '');
    intervalId = window.setInterval(() => {
      announcedText.value = bodyRef.value?.textContent?.trim() ?? '';
      writeToStatusAlert(announcedText.value);
    }, cadence);
  }

  function stopAnnounceInterval() {
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  onBeforeUnmount(() => {
    stopAnnounceInterval();
  });

  watch(
    () => props.modelValue,
    async (open) => {
      await nextTick();
      if (open) {
        openDialog();
      } else {
        closeDialog();
      }
    },
    { immediate: true },
  );
</script>

<template>
  <div
    v-if="modelValue && type !== 'modal'"
    class="mpf-dialog-backdrop"
    :class="{ 'mpf-dialog-backdrop--open': true }"
    aria-hidden="true"
    role="presentation"
  />

  <!-- WCAG Screen reader - role="alertdialog" so SR reads the sr-only text in the dialog as it changes -->
  <dialog
    ref="dialogRef"
    v-show="modelValue"
    class="mpf-dialog"
    :class="{
      'mpf-dialog--modal': type === 'modal',
      'mpf-dialog--dialog': type === 'dialog',
    }"
    :aria-labelledby="titleId"
    :aria-describedby="bodyId"
    aria-modal="true"
    aria-atomic="false"
    role="alertdialog"
    @keydown="onKeydown"
    @cancel="onCancelEvent"
    @mousedown="onBackdropMouseDown"
    tabindex="-1"
  >
    <header class="mpf-dialog__header">
      <h2 class="mpf-heading-m" :id="titleId">
        {{ titleText }}
      </h2>
    </header>

    <!-- SR text-->
    <section v-if="announceWhileOpenMs && announcedText" ref="straplineRef" class="sr-only">
      <div role="status" aria-live="polite" aria-atomic="true">
        {{ announcedText }}
      </div>
    </section>

    <!-- Main text-->
    <section
      ref="bodyRef"
      v-if="bodyText || strapline || slots.body"
      class="mpf-dialog__body"
      :id="bodyId"
    >
      <p v-if="strapline" class="mpf-dialog__strapline" :id="straplineId">
        {{ strapline }}
      </p>

      <slot name="body">
        <slot />
      </slot>
    </section>

    <footer class="mpf-dialog__footer">
      <slot name="actions" :close="() => requestClose('cancel')" :ok="onOkClick">
        <div class="mpf-dialog__actions">
          <button type="button" class="mpf-button mpf-button--secondary" @click="onCancelClick">
            {{ cancelText }}
          </button>
          <button type="button" class="mpf-button mpf-button--primary" @click="onOkClick">
            {{ okText }}
          </button>
        </div>
      </slot>
    </footer>
  </dialog>
</template>
